import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { ApiConfiguration } from '@/api/api-configuration';
import { LiveSessionService } from '@/api/services/live-session.service';
import { LiveSessionStatus } from '@/features/masterclasses/types/live-session.types';
import { FilterConfig, FilterType, ListService } from '@/shared/services/list.service';
import { ToastService } from '@/shared/services/toast.service';
import { toInfiniteQuery } from '@/shared/utils/infinitequery';

@Injectable({
  providedIn: 'any'
})
export class MasterclassesListService extends ListService {
  filterConfig: FilterConfig = {
    all_live_sessions: {
      value: `{"field":"live_session.status","operator":"is_not_null","value":""}`,
      type: FilterType.AND
    },
    active_live_sessions: {
      value: `{"field":"live_session.status","operator":"equal","value":"ACTIVE"}`,
      type: FilterType.AND
    },
    active_not_live_sessions: {
      value: `{"field":"live_session.status","operator":"equal","value":"INACTIVE"}`,
      type: FilterType.AND
    },
    draft_live_sessions: {
      value: `{"field":"live_session.is_published","operator":"equal","value":"false"}`,
      type: FilterType.AND
    }
  };
  searchArray = computed(() => {
    if (!this.search()) return [];

    return [`{"field":"course.name", "operator":"contains_ai_ci", "value":"${this.search()}"}`];
  });
  private apiService = inject(LiveSessionService);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);
  readonly query = injectQuery(() => ({
    queryKey: [
      'live_session',
      this.pageParam(),
      this.filtersString(),
      this.limit(),
      this.sortingString(),
      this.searchArray()
    ],
    enabled: this.isQueryActive(),
    queryFn: async ({ queryKey }) => {
      const page = queryKey[1];
      const limit = queryKey[3];
      const sort = queryKey[4];

      const filters = this.buildFilters(this.searchArray(), queryKey[2] as string, this.filterConfig);

      if (queryKey[2] === 'active_live_sessions') {
        filters.filter?.push(`{"field":"live_session.is_published","operator":"equal","value":"true"}`);
      }

      const params = this.getQueryParams({ page, limit, sort, filters });

      return lastValueFrom(this.apiService.apiLiveSessionOverviewGet(params).pipe(toInfiniteQuery(params)));
    }
  }));

  private createExportFileName(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `masterclass_export_${year}${month}${day}.csv`;
  }

  private downloadObjectUrlFromBlob(blob: Blob): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = this.createExportFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  }

  async getCsvExportUrl(): Promise<boolean> {
    try {
      const filters = this.buildFilters(this.searchArray(), this.filtersString() as string, this.filterConfig);
      const sort = this.sortingString();
      const params = this.getQueryParams({ page: undefined, limit: undefined, sort, filters });

      if (params['filter'] && Array.isArray(params['filter'])) {
        const parsed = JSON.parse(params['filter'][0]);
        if (parsed.value === LiveSessionStatus.ACTIVE) {
          params['filter'].push(`{"field":"live_session.is_published","operator":"equal","value":"true"}`);
        }
      }

      const queryString = new URLSearchParams();
      if (params['sort']) queryString.append('sort', String(params['sort']));
      if (params['filter']) {
        (params['filter'] as string[]).forEach((f: string) => queryString.append('filter', f));
      }
      if (params['filter_or']) {
        (params['filter_or'] as string[]).forEach((f: string) => queryString.append('filter_or', f));
      }

      const url = `${this.apiConfig.rootUrl}/api/live_session/csv-export?${queryString.toString()}`;
      const blob = await lastValueFrom(this.http.get(url, { responseType: 'blob' }));
      this.downloadObjectUrlFromBlob(blob);

      return true;
    } catch {
      this.toastService.error($localize`There was an error exporting the masterclasses`);

      return false;
    }
  }
}
